/* eslint react/forbid-prop-types:0 */

import React, { Component } from 'react';
import ExperimentalTextfield from 'app/common/react/ui/experimental/TextField';
import GestaltTooltipWrapper from 'app/common/react/components/growth/unauth/signup/GestaltTooltipWrapper/GestaltTooltipWrapper';
import i18n from 'app/common/lib/i18n';
import PropTypes from 'prop-types';
import Tooltip from 'app/common/react/components/growth/unauth/signup/Tooltip/Tooltip';
import { DefaultFontFamily } from 'app/common/react/components/common/FontFamily';
import { Icon, TextField } from 'gestalt';

const styles = {
  input: {
    backgroundColor: '#fff',
    border: 'solid 1px #ccc',
    WebkitBoxShadow: 'none',
    boxShadow: 'none',
    position: 'relative',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    borderRadius: '3px',
    fontWeight: 'bold',
    padding: '10px',
    WebkitBoxSizing: 'border-box',
    boxSizing: 'border-box',
    width: '100%',
    color: '#333',
    fontSize: '14px',
  },
  fieldset: {
    position: 'relative',
  },
  errorIcon: {
    position: 'absolute',
    right: '14px',
    top: '14px',
  },
  tooltipText: {
    overflowWrap: 'break-word',
    wordWrap: 'break-word',
    color: 'white',
    fontWeight: 'bold',
  },
  suggestedEmailSpan: {
    whiteSpace: 'nowrap',
  },
  suggestedEmailLink: {
    display: 'inline-block',
    font: 'inherit',
    fontWeight: 'bold',
    fontSize: '14',
    color: '#fff',
    cursor: 'pointer',
    textDecoration: 'underline',
    verticalAlign: 'top',
    outline: 'none',
    outlineOffset: '0',
  },
  fieldTitle: {
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    color: '#333',
    float: 'left',
    fontFamily: DefaultFontFamily,
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 'bold',
    margin: '15px 0px 5px 3px',
  },
  suggestedEmailTooltip: {
    width: 'match-content',
  },
};

export class TextInputField extends Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      initialFocus: this.props.focused || false,
      gainFocus: false,
    };
  }

  componentDidMount() {
    if (this.state.initialFocus) {
      this.focusInput();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { domainError, type } = this.props;

    if (nextProps.hasError && this.props.hasError === false) {
      this.setState({ gainFocus: true });
    } else {
      this.setState({ gainFocus: false });
    }
    if (domainError && type && type === 'email') {
      this.setState({ shouldTooltipOpen: true });
    }
  }

  componentDidUpdate() {
    if (this.state.gainFocus) {
      this.focusInput();
    }
  }

  focusInput() {
    // Default 'input' tag
    if (this._input) {
      this._input.focus();
    }

    // 11/11/17 HACK - Accessing the 'input' property from the Gestalt component to enable focusing because there
    // is no official API.
    if (this._gestaltInput && this._gestaltInput.textfield) {
      this._gestaltInput.textfield.focus();
    }
  }

  handleDomainErrorDismiss = () => {
    this.setState({ shouldTooltipOpen: false });
  };

  renderErrorIcon = (error) => {
    if (error) {
      return (
        <div className="InputField__errorIcon" style={styles.errorIcon}>
          <Icon
            accessibilityLabel={i18n._('Remove', 'Accessible label for error icon')}
            color="red"
            icon="remove"
            inline
            size={14}
          />
        </div>
      );
    }
    return undefined;
  };

  renderTooltip = (tooltip, hasError) => {
    if (!tooltip || !hasError) {
      return undefined;
    }

    if (this.props.useGestaltTooltip) {
      return <GestaltTooltipWrapper anchor={this._input} message={tooltip} />;
    } else {
      return <Tooltip message={tooltip} />;
    }
  };

  renderGestaltInputField = () => {
    const {
      autoComplete,
      disabled,
      domainError,
      id,
      inputRef,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      type,
      updateEmail,
      useExperimentalTextfield,
      value,
    } = this.props;

    const error = this.props.hasError || this.props.tooltip ? this.props.tooltip : '';

    if (useExperimentalTextfield) {
      return (
        <ExperimentalTextfield
          disabled={disabled}
          error={domainError || error}
          id={id}
          name={name}
          onBlur={onBlur}
          onChange={({ event, value }) => {
            onChange(event);
          }}
          onFocus={onFocus}
          placeholder={placeholder}
          ref={(comp) => {
            this._gestaltInput = comp;
            if (inputRef) {
              inputRef(comp);
            }
          }}
          type={type}
          onLabelTouch={updateEmail}
          value={value}
        />
      );
    } else {
      return (
        <TextField
          autoComplete={autoComplete}
          disabled={disabled}
          errorMessage={error}
          id={id}
          name={name}
          onBlur={onBlur}
          onChange={({ event, value }) => {
            onChange(event);
          }}
          onFocus={onFocus}
          placeholder={placeholder}
          ref={(comp) => {
            this._gestaltInput = comp;
            if (inputRef) {
              inputRef(comp);
            }
          }}
          type={type}
          updateEmail={updateEmail}
          value={value}
        />
      );
    }
  };

  renderVanillaTextInputField(inputClassName, inputStyles) {
    return (
      <div>
        <input
          aria-label={this.props.accessibilityLabel || this.props.placeholder}
          autoComplete={this.props.autoComplete}
          className={inputClassName}
          name={this.props.name}
          onBlur={this.props.onBlur}
          onFocus={this.props.onFocus}
          onChange={this.props.onChange}
          onClick={this.props.onClick}
          placeholder={this.props.placeholder}
          ref={(comp) => {
            this._input = comp;
            if (this.props.inputRef) {
              this.props.inputRef(comp);
            }
          }}
          style={inputStyles}
          type={this.props.type}
          value={this.props.value}
        />
        {this.renderErrorIcon(this.props.hasError)}
        {this.renderTooltip(this.props.tooltip, this.props.hasError)}
      </div>
    );
  }

  render() {
    let inputStyles = styles.input;
    if (this.props.inputStyleOverrides) {
      inputStyles = { ...inputStyles, ...this.props.inputStyleOverrides };
    }
    let fieldsetStyles = styles.fieldset;
    if (this.props.fieldsetStyleOverrides) {
      fieldsetStyles = { ...fieldsetStyles, ...this.props.fieldsetStyleOverrides };
    }

    let inputClassName = '';
    if (this.props.placeholderColor === 'gray') {
      inputClassName = 'UnauthTextInputField__gray';
    } else if (this.props.placeholderColor === 'darkGray') {
      inputClassName = 'UnauthTextInputField__darkGray';
    }

    return (
      <fieldset className={this.props.classNames} style={fieldsetStyles}>
        {this.props.inputFieldTitle ? (
          <div style={styles.fieldTitle}>{this.props.inputFieldTitle}</div>
        ) : null}
        {this.props.useGestalt
          ? this.renderGestaltInputField()
          : this.renderVanillaTextInputField(inputClassName, inputStyles)}
      </fieldset>
    );
  }
}

TextInputField.propTypes = {
  accessibilityLabel: PropTypes.string,
  autoComplete: PropTypes.oneOf(['on', 'off', 'username', 'new-password', 'current-password']),
  classNames: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  domainError: PropTypes.node,
  fieldsetStyleOverrides: PropTypes.object,
  focused: PropTypes.bool,
  hasError: PropTypes.bool,
  id: PropTypes.string.isRequired,
  inputStyleOverrides: PropTypes.object,
  inputFieldTitle: PropTypes.string,
  inputRef: PropTypes.func,
  name: PropTypes.string.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  placeholder: PropTypes.string,
  placeholderColor: PropTypes.oneOf(['gray', 'darkGray']),
  tooltip: PropTypes.node,
  type: PropTypes.oneOf(['url', 'text', 'password', 'email', 'number']).isRequired,
  updateEmail: PropTypes.func,
  useGestalt: PropTypes.bool,
  useGestaltTooltip: PropTypes.bool,
  useExperimentalTextfield: PropTypes.bool,
  value: PropTypes.string,
};

TextInputField.defaultProps = {
  autoComplete: 'on',
  hasError: false,
};

export default TextInputField;
